import type { AronAttachmentLimits } from "../types";

// Limity załączników w JEDNYM miejscu na froncie: picker modeli je wypisuje, kompozytor
// egzekwuje, a liczby przychodzą z backendu (services/modes.py → attachment_limits),
// gdzie są limitami DOSTAWCY danego trybu — nie własnym widzimisię appki.

const MB = 1_000_000;

/** „7,5 MB” / „22 MB” — po polsku, bez zbędnych miejsc po przecinku. */
export function fmtMb(bytes: number): string {
  const mb = bytes / MB;
  const s = mb >= 10 ? mb.toFixed(0) : mb.toFixed(1).replace(/\.0$/, "");
  return `${s.replace(".", ",")} MB`;
}

/** Jedna linia do karty modelu i dymka: co i ile ten tryb przyjmie. */
export function formatLimits(l: AronAttachmentLimits): string {
  const parts = [`do ${l.max_files} plików na wiadomość`];
  if (l.vision) {
    parts.push(`obraz ≤ ${fmtMb(l.image_max_bytes)}`);
    parts.push(
      `PDF ≤ ${fmtMb(l.file_max_bytes)}${l.pdf_max_pages ? ` i ${l.pdf_max_pages} stron` : ""}`,
    );
    parts.push(`obrazy + PDF-y razem ≤ ${fmtMb(l.request_max_bytes)} na zapytanie`);
  } else {
    parts.push("bez obrazów i PDF-ów (model tekstowy)");
  }
  parts.push(`DXF/DOCX/XLSX/TXT ≤ ${fmtMb(l.text_file_max_bytes)} (jako tekst)`);
  return parts.join(" · ");
}

export function limitsTitle(l: AronAttachmentLimits): string {
  return `Limity dostawcy (${l.provider_label}). ${l.source}`;
}

/** Powód odrzucenia JEDNEGO pliku przed wysyłką — null, gdy plik się mieści.
 *  `compress` = zaznaczone „Kompresuj obrazy” (obraz jedzie przeskalowany, więc
 *  mierzy go tylko sufit wgrania). */
export function attachmentProblem(
  name: string,
  ctype: string,
  size: number,
  l: AronAttachmentLimits | undefined,
  compress: boolean,
): string | null {
  if (!l) return null;
  if (ctype.startsWith("image/")) {
    if (!l.vision) return `${name}: model tej rozmowy nie czyta obrazów.`;
    if (!compress && size > l.image_max_bytes) {
      return (
        `${name} ma ${fmtMb(size)}, a limit tego modelu na obraz to ` +
        `${fmtMb(l.image_max_bytes)} (${l.provider_label}). Zaznacz „Kompresuj obrazy” ` +
        "albo pomniejsz plik."
      );
    }
    if (size > l.text_file_max_bytes) {
      return `${name} ma ${fmtMb(size)} — za dużo nawet do kompresji (max ${fmtMb(l.text_file_max_bytes)}).`;
    }
    return null;
  }
  if (ctype === "application/pdf") {
    if (!l.vision) return `${name}: model tej rozmowy nie czyta PDF-ów.`;
    if (size > l.file_max_bytes) {
      return (
        `${name} ma ${fmtMb(size)}, a limit tego modelu na PDF to ${fmtMb(l.file_max_bytes)} ` +
        `(${l.provider_label}). Podziel dokument albo wgraj go na TWO Drive i poproś ` +
        "ARON-a o analizę z dysku."
      );
    }
    return null;
  }
  if (size > l.text_file_max_bytes) {
    return `${name} ma ${fmtMb(size)}, a limit to ${fmtMb(l.text_file_max_bytes)}.`;
  }
  return null;
}

/** Obrazy (bez kompresji) i PDF-y jadą do modelu W JEDNYM zapytaniu — ich suma
 *  musi zmieścić się w limicie żądania dostawcy. */
export function inlineTotalProblem(
  files: { name: string; ctype: string; size: number }[],
  l: AronAttachmentLimits | undefined,
  compress: boolean,
): string | null {
  if (!l || !l.vision) return null;
  const total = files.reduce((sum, f) => {
    const inline =
      f.ctype === "application/pdf" || (f.ctype.startsWith("image/") && !compress);
    return sum + (inline ? f.size : 0);
  }, 0);
  if (total > l.request_max_bytes) {
    return (
      `Obrazy i PDF-y ważą razem ${fmtMb(total)}, a jedno zapytanie do modelu mieści ` +
      `${fmtMb(l.request_max_bytes)} (${l.provider_label}). Wyślij pliki w kilku ` +
      "wiadomościach albo wgraj je na TWO Drive i poproś ARON-a o analizę z dysku."
    );
  }
  return null;
}
